/**
 * The question kit behind every form an administrator builds for an event — the registration
 * form and the post-event feedback form. Both store the same question shape and answer it the same
 * way; they differ only in what surrounds the questions.
 *
 * The backend loads this file at runtime, so it must not import anything but types.
 */

export const FORM_QUESTION_TYPES = [
  'text',
  'textarea',
  'select',
  'multiselect',
  'checkbox',
  'radio',
  'number',
  'rating',
  'date',
] as const;

export type FormQuestionType = (typeof FORM_QUESTION_TYPES)[number];

/** The types answered by picking from `options`. */
export const FORM_CHOICE_TYPES = [
  'select',
  'multiselect',
  'checkbox',
  'radio',
] as const satisfies readonly FormQuestionType[];

/** The choice types that take several picks, answered with an array. */
export const FORM_MULTI_CHOICE_TYPES = [
  'multiselect',
  'checkbox',
] as const satisfies readonly FormQuestionType[];

/**
 * The top of every rating, from 1: a feedback form's overall rating and any `rating` question on
 * any form.
 */
export const FORM_RATING_MAX = 5;

/** Text and choices are strings, number and rating are numbers, multi-choice is an array. */
export type FormAnswerValue = string | number | string[];

export interface FormQuestion {
  id: string;
  label: string;
  type: FormQuestionType;
  options: string[];
  required: boolean;
  sortOrder: number;
}

export interface FormSection {
  id: string;
  title: string;
  description: string;
  sortOrder: number;
  questions: FormQuestion[];
}

/** A form's questions in the order they are asked: the ungrouped ones, then each section. */
export interface FormQuestionSet {
  ungroupedQuestions: FormQuestion[];
  sections: FormSection[];
}

export interface FormQuestionInput {
  label: string;
  type: FormQuestionType;
  options?: string[];
  required?: boolean;
}

export interface FormSectionInput {
  title: string;
  description?: string;
  /** At least one: a heading with nothing under it is refused. */
  questions: FormQuestionInput[];
}

export interface FormAnswerInput {
  questionId: string;
  value: FormAnswerValue;
}

/**
 * An answer as stored: the question it answered is copied in beside the value, so the answer
 * still reads correctly after the question is edited or removed.
 */
export interface StoredFormAnswer {
  questionId: string;
  label: string;
  type: FormQuestionType;
  value: FormAnswerValue;
}
